import React, { useRef, useEffect } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import {
  Dropdown,
  Toggle,
  ButtonIcon,
  ButtonGroup,
  Grid,
  Card,
  CardBody,
  Button,
} from '@statsbomb/kitbag-components';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import Tile from '../../../components/Tile/Tile';
import useQueryString from '../../../utils/hooks/useQueryString';
import { GET_PASS_PLACEMENT_EVENTS } from './getPassPlacementEvents';
import { GET_CATCH_PLACEMENT_EVENTS } from './getCatchPlacementEvents';
import { mf_PlayerDetails, mf_Players, mf_TeamDetails } from '../../../apollo';
import {
  StyledPlayerPassPlacement,
  PassPlacementChartContainer,
} from './PlayerPassPlacement.styles';
import PassPlacement from '../../../visualisations/PassPlacement/PassPlacement';
import FieldEvent from '../../../visualisations/FieldEvent/FieldEvent';
import {
  PASS_PLACEMENT_COLOR_OPTIONS,
  PASS_PLACEMENT_COLOR_MODES,
  PASS_PLACEMENT_SCALES,
  PASS_PLACEMENT_SCALE_DEFAULTS,
  PASS_PLACEMENT_SCALE_OPTIONS,
} from '../../../visualisations/PassPlacement/PassPlacement.constants';
import KeyButton from '../../../components/buttons/DisplayKey/DisplayKey';
import {
  FixedAside,
  FixedAsideContent,
  GridBackground,
  KitbagPageGridHolder,
} from '../../../components/Layout/Layout.styles';
import ExportModal from '../../../components/ExportModal/ExportModal';
import { useExportSeasonInfo } from '../../../utils/hooks/useExportSeasonInfo';
import PageHeader from '../../../components/PageHeader/PageHeader';
import AccordionTile from '../../../components/Accordion/AccordionTile';
import { DropdownOverride } from '../../../components/DropdownFilter/DropdownFilter.styles';
import {
  playerDetailsPropTypes,
  teamDetailsPropTypes,
} from '../../../apollo/index.data.propTypes';
import { PASS_PLACEMENT_SIDEPANEL_WIDTH } from './PlayerPassPlacement.constants';
import { MemoizedVideoTile } from '../../../components/Video/VideoTile';
import {
  formatPassingVideoData,
  getCurrentPlayData,
} from '../PlayerPassing/PlayerPassing.dataManipulation';
import { getSelectedEventDetails } from '../PlayerPassing/PassInfo';
import { getEnrichedTheme } from '../../../utils/visualisations/visPalettes';
import { useStore } from '../../../store/useStore';
import PassSelectedEvent from '../PlayerPassing/PassSelectedEvent';
import { getPassPlacementCardState } from './PlayerPassPlacement.helpers';
import PlaylistButton from '../../../components/buttons/Playlist/Playlist';
import { CardButtons, CardHeightWrapper } from '../../../components/Card/card';

const PlayerPassPlacement = ({
  __storybookPlayerDetails,
  __storybookTeamDetails,
  __storybookInitialShowCatcher = true,
  __storybookInitialShowFailsRing = true,
  __storybookInitialShowBallSize = true,
  __storybookInitialColourMode = PASS_PLACEMENT_COLOR_MODES.PROXIMITY,
  __storybookColPadding = null,
}) => {
  const [showCatcher, setShowCatcher] = useQueryString(
    'catcher',
    __storybookInitialShowCatcher
  );
  const [showFailsRing, setShowFailsRing] = useQueryString(
    'fails',
    __storybookInitialShowFailsRing
  );
  const [showBallSize, setShowBallSize] = useQueryString(
    'ball',
    __storybookInitialShowBallSize
  );
  const [aspectRatio, setAspectRatio] = useQueryString(
    'aspect',
    PASS_PLACEMENT_SCALE_DEFAULTS.ASPECT_RATIO
  );
  const [xZoomIndex, setXZoomIndex] = useQueryString(
    'xzoom',
    PASS_PLACEMENT_SCALE_DEFAULTS.DEFAULT_PLUS_MINUS_X_INDEX
  );
  const [displayKey, setDisplayKey] = useQueryString('key', true);
  const [colorMode, setColorMode] = useQueryString(
    'mode',
    __storybookInitialColourMode
  );

  const selectedPlayerId = useReactiveVar(mf_Players);

  const mf_PlayerDetailsRV =
    __storybookPlayerDetails || useReactiveVar(mf_PlayerDetails);
  const mf_TeamDetailsRV =
    __storybookTeamDetails || useReactiveVar(mf_TeamDetails);
  const [showPasses, setShowPasses] = useQueryString(
    'show',
    mf_PlayerDetailsRV?.mostCommonPosition?.code === undefined ||
      mf_PlayerDetailsRV?.mostCommonPosition?.code === 'QB'
  );

  const {
    loading: loadingPasses,
    error: errorPasses,
    data: dataPasses,
  } = useQuery(GET_PASS_PLACEMENT_EVENTS);
  const {
    loading: loadingCatches,
    error: errorCatches,
    data: dataCatches,
  } = useQuery(GET_CATCH_PLACEMENT_EVENTS);

  const loading = showPasses ? loadingPasses : loadingCatches;
  const error = showPasses ? errorPasses : errorCatches;
  const data = showPasses ? dataPasses : dataCatches;

  const playerName = mf_PlayerDetailsRV?.name;
  const teamName = mf_TeamDetailsRV?.name;
  const { competitionSeason: info2, seasonName } = useExportSeasonInfo({
    asObject: true,
  });

  const handleShowCatcher = () => {
    setShowCatcher(!showCatcher);
  };

  const handleShowFailsRing = () => {
    setShowFailsRing(!showFailsRing);
  };

  const handleBallSize = () => {
    setShowBallSize(!showBallSize);
  };

  const resetZoom = () => {
    setXZoomIndex(
      PASS_PLACEMENT_SCALES.find((r) => r.ASPECT_RATIO === aspectRatio)
        .DEFAULT_PLUS_MINUS_X_INDEX
    );
  };

  const handleAspectRatioChange = (option) => {
    setAspectRatio(option.value);
    setXZoomIndex(
      PASS_PLACEMENT_SCALES.find((r) => r.ASPECT_RATIO === option.value)
        .DEFAULT_PLUS_MINUS_X_INDEX
    );
  };

  const selectedAspectRatio = PASS_PLACEMENT_SCALES.find(
    (r) => r.ASPECT_RATIO === aspectRatio
  );

  const decreaseScale = () => {
    if (xZoomIndex < selectedAspectRatio.PLUS_MINUS_X_OPTIONS.length - 1) {
      setXZoomIndex(xZoomIndex + 1);
    }
  };
  const increaseScale = () => {
    if (xZoomIndex > 0) {
      setXZoomIndex(xZoomIndex - 1);
    }
  };

  const handleDisplayKey = () => {
    setDisplayKey(!displayKey);
  };

  const selectedEvent = useStore((state) => state.passPlacementSelectedEvent);
  const setSelectedEvent = useStore(
    (state) => state.setPassPlacementSelectedEvent
  );
  const selectedEvents = useStore((state) => state.passPlacementSelectedEvents);
  const setSelectedEvents = useStore(
    (state) => state.setPassPlacementSelectedEvents
  );
  const selectEventAndOpenVideo = useStore(
    (state) => state.selectEventAndOpenVideo
  );
  const clearSelectionAndCloseVideo = useStore(
    (state) => state.clearSelectionAndCloseVideo
  );
  const isDragActive = useStore((state) => state.isPassPlacementDragActive());
  const setPlayAll = useStore((state) => state.setPassPlacementPlayAllActive);
  const isVideoVisible = useStore((state) => state.passPlacementVideoOpen);
  const setIsVideoVisible = useStore(
    (state) => state.setPassPlacementVideoOpen
  );

  const plusMinusX = selectedAspectRatio.PLUS_MINUS_X_OPTIONS[xZoomIndex];

  // player colours for highlighting
  const theme = useTheme();
  const enrichedTheme = getEnrichedTheme(theme);
  const visPaletteEnriched = enrichedTheme.colours.visualisations;
  const videoActive = useRef(false);

  // returns array of event objects
  const getVideoTileData = () => {
    // if the user has a individual selected event
    if (selectedEvent?.play?.id && selectedEvents.length === 0) {
      return formatPassingVideoData([selectedEvent], visPaletteEnriched);
    }
    // if the users clicks play all (typically on first load) without any active drag or selection
    if (
      data &&
      !selectedEvent &&
      selectedEvents.length === 0 &&
      videoActive.current
    ) {
      const filteredEvents = data.passEvents.edges.map((edge) => edge.node);
      // Set selectedEvents for playlist functionality
      setPlayAll(true);
      setSelectedEvents(
        filteredEvents.map((event) => event.play?.id).filter(Boolean)
      );
      return formatPassingVideoData(filteredEvents, visPaletteEnriched);
    }
    // return the events within a marquee selection
    if (data && selectedEvents.length > 0) {
      const filteredEvents = data.passEvents.edges
        .filter((edge) => selectedEvents.includes(edge.node.play?.id))
        .map((edge) => edge.node);
      return formatPassingVideoData(filteredEvents, visPaletteEnriched);
    }
    return [];
  };

  const onDragSelection = (boundEvents) => {
    setSelectedEvents(boundEvents);
  };

  // single selection callback
  const onSelectedEvent = (evtId) => {
    if (evtId) {
      selectEventAndOpenVideo(evtId);
      // clear multiple selects
      setSelectedEvents([]);
    } else {
      setSelectedEvent(null);
      setSelectedEvents([]);
      setIsVideoVisible(false);
    }
  };

  // callback from video player playlist hook
  const onPlaylistChange = (currentlyPlayingPlay) => {
    // if multiple select state
    if (selectedEvents?.length > 0) {
      setSelectedEvent(
        data?.passEvents?.edges?.find(
          (event) => event?.node?.play?.id === currentlyPlayingPlay?.id
        )?.node
      );
    }
  };

  // check if player changes, if so close video and clear selection
  useEffect(() => {
    if (selectedPlayerId) {
      clearSelectionAndCloseVideo();
    }
  }, [selectedPlayerId]);

  return (
    <StyledPlayerPassPlacement>
      <KitbagPageGridHolder>
        <Grid container={false} page>
          <FixedAside
            $width={`${!isVideoVisible ? PASS_PLACEMENT_SIDEPANEL_WIDTH : 0}px`}
            $padding={__storybookColPadding || '1rem 0 6.5rem 0'}
            $paddingTopMD="1rem"
          >
            <GridBackground>
              <AccordionTile
                header={<Tile.AccordionHeader>General</Tile.AccordionHeader>}
                body={
                  <Tile.AccordionBody $gap="0.5rem">
                    <Toggle
                      id="show-passes-toggle"
                      label={
                        showPasses
                          ? `Passes from ${playerName}`
                          : `Passes to ${playerName}`
                      }
                      onChange={() => setShowPasses(!showPasses)}
                      checked={showPasses}
                      size="small"
                    />
                    <Toggle
                      id="show-catcher-toggle"
                      label="Show catcher"
                      onChange={handleShowCatcher}
                      checked={showCatcher}
                      size="small"
                    />
                    <Toggle
                      id="show-fail-rings-toggle"
                      label="Show incomplete passes as rings"
                      onChange={handleShowFailsRing}
                      checked={showFailsRing}
                      size="small"
                    />
                    <Toggle
                      id="show-ball-size-toggle"
                      label="Approximate ball size"
                      onChange={handleBallSize}
                      checked={showBallSize}
                      size="small"
                    />
                    <DropdownOverride>
                      <Dropdown
                        id="color-mode-dropdown"
                        options={PASS_PLACEMENT_COLOR_OPTIONS}
                        label="Color Mode"
                        onChange={(selectedOption) =>
                          setColorMode(selectedOption.value)
                        }
                        value={PASS_PLACEMENT_COLOR_OPTIONS.find(
                          (item) => item.value === colorMode
                        )}
                        isSearchable={false}
                        menuPosition="static"
                        size="small"
                        testId="color-mode-dropdown"
                      />
                    </DropdownOverride>
                    <DropdownOverride>
                      <Dropdown
                        id="aspect-ratio-dropdown"
                        options={PASS_PLACEMENT_SCALE_OPTIONS}
                        label="Chart Area Display"
                        onChange={handleAspectRatioChange}
                        value={PASS_PLACEMENT_SCALE_OPTIONS.find(
                          (item) => item.value === aspectRatio
                        )}
                        isSearchable={false}
                        menuPosition="static"
                        size="small"
                        testId="aspect-ratio-dropdown"
                      />
                    </DropdownOverride>
                  </Tile.AccordionBody>
                }
                isExpandedDefault
                isMount
              />
            </GridBackground>
          </FixedAside>
          <FixedAsideContent
            $margin="0.5rem 0.5rem 0 0"
            $marginMd={`0.5rem 0.5rem -3.5px ${
              !isVideoVisible ? PASS_PLACEMENT_SIDEPANEL_WIDTH - 2 : 0
            }px`}
          >
            <Grid container={false}>
              <Grid item xs={isVideoVisible ? 6 : 12}>
                <PageHeader
                  href="/player/overview/:leagues/:seasons/:teams/:players"
                  rootPage="Player"
                  activePage="Pass Placement"
                  margin="0 0 0.5rem 0"
                  __storybookPlayerDetails={__storybookPlayerDetails}
                  __storybookTeamDetails={__storybookTeamDetails}
                >
                  <div className="buttons">
                    <ExportModal
                      title={playerName}
                      secondaryTitle="Pass Placement"
                      info1={teamName}
                      info2={info2}
                      fileName={`${playerName} Pass Placement Chart ${seasonName}`}
                      isDisabled={
                        loading || error || !data?.passEvents?.edges.length
                      }
                    >
                      <div>
                        <PassPlacement
                          data={data}
                          showCatcher={showCatcher}
                          showFailsRing={showFailsRing}
                          showBallSize={showBallSize}
                          colorMode={colorMode}
                          selectedPlay={setSelectedEvent}
                          aspectRatioName={aspectRatio}
                          plusMinusX={plusMinusX}
                          showKey={displayKey}
                        />
                        {selectedEvent && (
                          <FieldEvent selectedPlay={selectedEvent} />
                        )}
                      </div>
                    </ExportModal>
                  </div>
                </PageHeader>
                <CardHeightWrapper>
                  <Card>
                    <CardBody
                      statusType={
                        getPassPlacementCardState(loading, Boolean(error), data)
                          .state
                      }
                      statusTitle={
                        getPassPlacementCardState(loading, Boolean(error), data)
                          .title
                      }
                      statusDescription={
                        getPassPlacementCardState(loading, Boolean(error), data)
                          .description
                      }
                    >
                      <PassPlacementChartContainer $maxWidth="1100px">
                        <CardButtons>
                          {isDragActive && selectedEvents.length > 0 && (
                            <Button
                              onClick={clearSelectionAndCloseVideo}
                              variant="secondary"
                              size="small"
                              title="Clear Selection"
                            >
                              Clear Selection
                            </Button>
                          )}
                          <PlaylistButton
                            handleClick={() => {
                              if (selectedEvents.length === 0) {
                                videoActive.current = true;
                              }
                              setIsVideoVisible(true);
                            }}
                            numberOfPlays={
                              selectedEvents.length > 0
                                ? selectedEvents.length
                                : data?.passEvents?.edges?.length
                            }
                            isDisabled={
                              data?.passEvents?.edges?.length === 0 ||
                              loading ||
                              isVideoVisible
                            }
                            variant={isDragActive ? 'primary' : 'secondary'}
                          />
                          <ButtonGroup>
                            <ButtonIcon
                              id="zoom-button-out"
                              onClick={decreaseScale}
                              variant="secondary"
                              icon="ZoomOut"
                              size="small"
                              title="zoom out"
                            />
                            <ButtonIcon
                              id="zoom-button-in"
                              onClick={increaseScale}
                              variant="secondary"
                              icon="ZoomIn"
                              size="small"
                              title="zoom in"
                            />
                            <ButtonIcon
                              id="zoom-button-reset"
                              onClick={resetZoom}
                              variant="secondary"
                              icon="ResetZoom"
                              size="small"
                              title="reset zoom"
                            />
                          </ButtonGroup>
                          <KeyButton
                            showKey={displayKey}
                            handleShowKey={handleDisplayKey}
                          />
                        </CardButtons>
                        <PassPlacement
                          data={data}
                          showCatcher={showCatcher}
                          showFailsRing={showFailsRing}
                          showBallSize={showBallSize}
                          colorMode={colorMode}
                          showKey={displayKey}
                          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                          selectedPlay={selectedEvent}
                          handleEventSelection={onSelectedEvent}
                          aspectRatioName={aspectRatio}
                          plusMinusX={plusMinusX}
                          handleDragSelection={onDragSelection}
                          selectedPlays={selectedEvents}
                          redrawSelection={isDragActive}
                        />
                        <FieldEvent
                          selectedPlay={selectedEvent}
                          margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                        />
                      </PassPlacementChartContainer>
                    </CardBody>
                  </Card>
                </CardHeightWrapper>
              </Grid>

              <Grid item xs={isVideoVisible ? 6 : 12}>
                <>
                  {isVideoVisible && (
                    <MemoizedVideoTile
                      title={
                        getCurrentPlayData(
                          getSelectedEventDetails(selectedEvent)
                        )?.title
                      }
                      subTitle={
                        getCurrentPlayData(
                          getSelectedEventDetails(selectedEvent)
                        )?.game
                      }
                      data={getVideoTileData()}
                      // eslint-disable-next-line max-len
                      gotoLink={`/game/animation/:leagues/:seasons/:teams/${selectedEvent?.game?.id}/${selectedEvent?.play?.drive?.id}/${selectedEvent?.play?.id}`}
                      isGotoDisabled={
                        !selectedEvent?.game?.id ||
                        !selectedEvent?.play?.drive?.id ||
                        !selectedEvent?.play?.id
                      }
                      handlePlaylistChange={onPlaylistChange}
                      fileName="Pass Completion Chart"
                      selectedPlayId={selectedEvent?.play?.id}
                      handleClose={clearSelectionAndCloseVideo}
                    >
                      <PassSelectedEvent
                        data={getCurrentPlayData(
                          getSelectedEventDetails(selectedEvent)
                        )}
                      />
                    </MemoizedVideoTile>
                  )}
                </>
              </Grid>
            </Grid>
          </FixedAsideContent>
        </Grid>
      </KitbagPageGridHolder>
    </StyledPlayerPassPlacement>
  );
};

PlayerPassPlacement.propTypes = {
  __storybookPlayerDetails: playerDetailsPropTypes,
  __storybookTeamDetails: teamDetailsPropTypes,
  __storybookInitialShowCatcher: PropTypes.bool,
  __storybookInitialShowFailsRing: PropTypes.bool,
  __storybookInitialShowBallSize: PropTypes.bool,
  __storybookInitialColourMode: PropTypes.oneOf(
    Object.values(PASS_PLACEMENT_COLOR_MODES)
  ),
  __storybookColPadding: PropTypes.string,
};

export default PlayerPassPlacement;
